import { AnalyticsOutlined, PersonOutlined, QueryStats, StorefrontOutlined } from '@mui/icons-material';
import ArrowDropDownOutlined from '@mui/icons-material/ArrowDropDownOutlined';
import FileUploadOutlined from '@mui/icons-material/FileUploadOutlined';
import GroupOutlined from '@mui/icons-material/GroupOutlined';
import { Button, Menu, MenuItem, Stack, type SxProps, type Theme } from '@mui/material';
import React, { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { ROUTES } from '@/routes';
import { useTheme } from '@/theme';

// Define a type for the active item key for type safety
type ActiveItemKey = 'users' | 'inventory' | 'distributors' | 'budgets' | 'import' | 'audit';

const ActionToolbar: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  // State to manage the anchor element for the Import menu
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const isImportMenuOpen = Boolean(anchorEl);

  const getActiveItem = (): ActiveItemKey => {
    const { pathname } = location;

    if (pathname === ROUTES.USERS || pathname === ROUTES.USER_DETAILS) {
      return 'users';
    }
    if (pathname === ROUTES.ALLOCATIONS) {
      return 'inventory';
    }
    if (pathname === ROUTES.DISTRIBUTORS) {
      return 'distributors';
    }
    if (pathname === ROUTES.BUDGETS) {
      return 'budgets';
    }
    if (pathname === ROUTES.AUDIT) {
      return 'audit';
    }

    const startsWith = (route: string) => pathname.startsWith(route.replace(/:\w+$/, ''));

    // Inventory routes
    if (startsWith(ROUTES.ALLOCATIONS_MANAGE.split('/:')[0])) {
      return 'inventory';
    }

    // Distributor routes
    if (startsWith(ROUTES.DISTRIBUTORS_MANAGE.split('/:')[0])) {
      return 'distributors';
    }

    // Budget routes
    if (startsWith(ROUTES.BUDGETS_MANAGE.split('/:')[0])) {
      return 'budgets';
    }

    // Import routes
    if (
      startsWith(ROUTES.IMPORT_ALLOCATION_GROUP_RELATIONSHIPS.split('/:')[0]) ||
      startsWith(ROUTES.IMPORT_DISTRIBUTOR_GROUP_RELATIONSHIPS.split('/:')[0]) ||
      startsWith(ROUTES.IMPORT_BUDGET_GROUP_RELATIONSHIPS.split('/:')[0])
    ) {
      return 'import';
    }

    return 'users';
  };

  const activeItem = getActiveItem();

  const handleItemClick = (itemKey: ActiveItemKey) => {
    switch (itemKey) {
      case 'users':
        navigate(ROUTES.USERS);
        break;
      case 'inventory':
        navigate(ROUTES.ALLOCATIONS);
        break;
      case 'distributors':
        navigate(ROUTES.DISTRIBUTORS, { state: { resetPaging: true } });
        break;
      case 'budgets':
        navigate(ROUTES.BUDGETS);
        break;
      case 'audit':
        navigate(ROUTES.AUDIT);
        break;
      default:
        navigate(ROUTES.HOME);
        break;
    }
  };

  const handleImportMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleImportMenuClose = () => {
    setAnchorEl(null);
  };

  const handleImportDistributorGroups = () => {
    handleImportMenuClose();
    navigate(ROUTES.IMPORT_DISTRIBUTOR_GROUP_RELATIONSHIPS);
  };

  const handleImportBudgetGroups = () => {
    handleImportMenuClose();
    navigate(ROUTES.IMPORT_BUDGET_GROUP_RELATIONSHIPS);
  };

  const handleImportAllocationGroups = () => {
    handleImportMenuClose();
    navigate(ROUTES.IMPORT_ALLOCATION_GROUP_RELATIONSHIPS);
  };

  const commonButtonSx: SxProps<Theme> = useMemo(
    () => ({
      color: 'text.primary',
      textTransform: 'none',
      padding: '0rem 20px',
      height: '3.6rem',
      whiteSpace: 'nowrap',
      fontWeight: theme.hhglobal.typography.fontWeights.regular,
      ':hover': {
        backgroundColor: theme.hhglobal.purpose.fillWeak,
      },
      '& .MuiSvgIcon-root': {
        width: '20px',
        height: '20px',
        aspectRatio: '1/1',
      },
    }),
    [theme],
  );

  const getButtonSx = (itemKey: ActiveItemKey): SxProps<Theme> => ({
    ...commonButtonSx,
    fontWeight: activeItem === itemKey ? 'bold' : 'normal',
  });

  return (
    <Stack
      direction='row'
      spacing={3}
      sx={{
        justifyContent: 'flex-start',
        alignItems: 'center',
        padding: '0rem 0.4rem',
      }}
    >
      <Button
        id='users-button'
        data-testid='manage-users-button'
        variant='text'
        startIcon={<PersonOutlined />}
        sx={getButtonSx('users')}
        onClick={() => handleItemClick('users')}
      >
        User Dashboard
      </Button>

      <Button
        id='inventory-button'
        data-testid='manage-inventory-button'
        variant='text'
        startIcon={<GroupOutlined />}
        sx={getButtonSx('inventory')}
        onClick={() => handleItemClick('inventory')}
      >
        Inventory Allocations
      </Button>

      <Button
        id='distributors-button'
        data-testid='manage-distributors-button'
        variant='text'
        startIcon={<StorefrontOutlined />}
        sx={getButtonSx('distributors')}
        onClick={() => handleItemClick('distributors')}
      >
        Distributors
      </Button>

      <Button
        id='budgets-button'
        data-testid='manage-budgets-button'
        variant='text'
        startIcon={<AnalyticsOutlined />}
        sx={getButtonSx('budgets')}
        onClick={() => handleItemClick('budgets')}
      >
        Budgets
      </Button>

      <Button
        id='import-button' // Accessibility
        data-testid='import-button'
        variant='text'
        startIcon={<FileUploadOutlined />}
        endIcon={<ArrowDropDownOutlined />}
        sx={getButtonSx('import')}
        onClick={handleImportMenuOpen}
        aria-controls={isImportMenuOpen ? 'import-menu' : undefined}
        aria-haspopup='true'
        aria-expanded={isImportMenuOpen ? 'true' : undefined}
      >
        Import
      </Button>

      <Menu
        id='import-menu'
        anchorEl={anchorEl}
        open={isImportMenuOpen}
        disableScrollLock={true}
        onClose={handleImportMenuClose}
        data-testid='import-menu'
        slotProps={{
          paper: {
            sx: {
              borderRadius: '4px',
              border: `0.5px solid ${theme.hhglobal.neutral.gray300}`,
              background: theme.palette.background.paper,
              boxShadow: '0 0 2px 1px rgba(0, 0, 0, 0.20)',
              padding: '0px',
            },
          },
          list: {
            'aria-labelledby': 'import-button',
            sx: {
              paddingTop: '5px',
              paddingBottom: '5px',
            },
          },
        }}
      >
        <MenuItem
          onClick={handleImportAllocationGroups}
          data-testid='import-menu-item-inventory-allocations'
          sx={{
            display: 'flex',
            padding: '4px 16px',
            alignItems: 'center',
            gap: '4px',
            fontSize: '14px',
            fontWeight: 400,
            '&:hover': {
              background: theme.hhglobal.purpose.fillWeak,
            },
          }}
        >
          Inventory Allocation Relationships
        </MenuItem>
        <MenuItem
          onClick={handleImportDistributorGroups}
          data-testid='import-menu-item-distributors'
          sx={{
            display: 'flex',
            padding: '4px 16px',
            alignItems: 'center',
            gap: '4px',
            fontSize: '14px',
            fontWeight: 400,
            '&:hover': {
              background: theme.hhglobal.purpose.fillWeak,
            },
          }}
        >
          Distributor Group Relationships
        </MenuItem>
        <MenuItem
          onClick={handleImportBudgetGroups}
          data-testid='import-menu-item-budgets'
          sx={{
            display: 'flex',
            padding: '4px 16px',
            alignItems: 'center',
            gap: '4px',
            fontSize: '14px',
            fontWeight: 400,
            '&:hover': {
              background: theme.hhglobal.purpose.fillWeak,
            },
          }}
        >
          Budget Group Relationships
        </MenuItem>
      </Menu>

      <Button
        id='audit-button'
        data-testid='audit-button'
        variant='text'
        startIcon={<QueryStats />}
        sx={getButtonSx('audit')}
        onClick={() => handleItemClick('audit')}
      >
        Audit
      </Button>
    </Stack>
  );
};

export default ActionToolbar;
